import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, Globe } from "lucide-react";
import { scrollToId } from "@/lib/site";
import { Reveal, SectionLabel, GlowButton } from "@/components/primitives";

const TAGS = [
  { label: "FAST", color: "#2563EB", pos: "-left-3 top-10" },
  { label: "RESPONSIVE", color: "#FF6B35", pos: "-right-4 top-24" },
  { label: "CONVERSION READY", color: "#65A30D", pos: "-left-4 bottom-10" },
];

export default function WebsiteShowcase() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [reduce ? 0 : 50, reduce ? 0 : -50]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#F8FAFC] py-20 sm:py-28 lg:py-32" data-testid="website-showcase-section">
      <div className="pointer-events-none absolute right-0 top-10 h-64 w-64 rounded-full bg-[#7C3AED]/10 blur-3xl" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <div className="flex justify-center">
            <SectionLabel color="#2563EB">Website Development</SectionLabel>
          </div>
          <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#111827] sm:text-5xl lg:text-6xl">
            Your website should work
            <br />
            <span className="text-gradient">as hard as you do.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <motion.div style={{ y }} className="relative mx-auto mt-14 max-w-4xl">
            {/* decorative shapes */}
            <div className="absolute -left-8 -top-8 h-24 w-24 rounded-full bg-[#FF6B35]/20" />
            <div className="absolute -bottom-8 -right-6 h-20 w-20 rounded-[1.5rem] bg-[#B7F34A] rotate-12" />

            <div className="overflow-hidden rounded-2xl bg-white shadow-float ring-1 ring-slate-100">
              <div className="flex items-center gap-1.5 border-b border-slate-100 px-5 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF6B35]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#FFC94A]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#65A30D]" />
                <span className="ml-3 flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1">
                  <Globe className="h-3 w-3 text-slate-400" />
                  <span className="text-[11px] text-slate-400">yourbrand.com</span>
                </span>
              </div>
              <div className="grid grid-cols-1 gap-0 sm:grid-cols-5">
                {/* sidebar/hero copy */}
                <div className="col-span-3 p-6 sm:p-8">
                  <div className="h-3 w-24 rounded-full bg-[#2563EB]" />
                  <div className="mt-4 h-8 w-5/6 rounded-lg bg-slate-200" />
                  <div className="mt-2 h-8 w-2/3 rounded-lg bg-slate-100" />
                  <div className="mt-3 h-3 w-full rounded-full bg-slate-100" />
                  <div className="mt-1.5 h-3 w-4/5 rounded-full bg-slate-100" />
                  <div className="mt-5 flex gap-3">
                    <div className="h-10 w-32 rounded-full bg-gradient-to-r from-[#2563EB] to-[#7C3AED]" />
                    <div className="h-10 w-24 rounded-full ring-1 ring-slate-200" />
                  </div>
                </div>
                {/* visual block */}
                <div className="col-span-2 flex items-center justify-center bg-gradient-to-br from-[#EEF2FF] to-[#FFF1EB] p-6">
                  <div className="relative h-32 w-32">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#2563EB] to-[#7C3AED] opacity-20" />
                    <div className="absolute inset-4 rounded-[1.5rem] bg-white shadow-card" />
                    <div className="absolute inset-9 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#7C3AED]" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 border-t border-slate-100 p-6 sm:p-8">
                {[0, 1, 2].map((n) => (
                  <motion.div key={n} className="h-20 rounded-xl bg-slate-50 ring-1 ring-slate-100" animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2.4, repeat: Infinity, delay: n * 0.3 }} />
                ))}
              </div>
            </div>

            {/* floating tags */}
            {TAGS.map((t) => (
              <motion.span
                key={t.label}
                className={`absolute ${t.pos} rounded-full bg-white px-3.5 py-1.5 text-xs font-bold shadow-float ring-1 ring-slate-100`}
                style={{ color: t.color }}
                animate={reduce ? {} : { y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                {t.label}
              </motion.span>
            ))}
          </motion.div>
        </Reveal>

        <Reveal delay={0.2} className="mt-14 flex justify-center">
          <GlowButton onClick={() => scrollToId("contact")} data-testid="website-showcase-cta">
            Build My Website
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </GlowButton>
        </Reveal>
      </div>
    </section>
  );
}
